import React from "react";

import "./sidebar.scss";

import {Accordion, AccordionTitleProps, Button, Divider, Menu, Sidebar as SemanticSidebar} from "semantic-ui-react";

import {SidebarConfiguration, SidebarControlsConfiguration} from "../../config/sidebar-configuration";
import {UiState} from "../../core/ui-state";
import {AppState} from "../../stores/app-mobx";
import {Legend} from "./legend/legend";
import {Legend as CoreLegend} from "../../core/components/legend/legend";
import {LayerList} from "../../core/components/layer-list/layer-list";
import {ViewManager} from "../../core/components/view-manager/view-manager";
import {Printing} from "../../core/components/printing/printing";
import {Sharing} from "../../core/components/sharing/sharing";
import {Icon} from "../../core/components/icon/icon";

import {MapState} from "../../core/map-state";
import {observer} from "mobx-react";
import {AppConfiguration} from "../../core/config/app-configuration";
import {MapViewerConfiguration} from "../../config/map-viewer-configuration";
import {autorun, runInAction} from "mobx";
import {Tools} from "../../core/components/tools/tools";

interface CollapseButtonProps {
    onClick: any;
    visible: boolean;
}

const CollapseButton = (props: CollapseButtonProps) => (
    <Button icon size={"mini"} attached="bottom"
        className={`geo-sidebar-collapse-button ${props.visible ? "open" : "closed"}`}
        onClick={props.onClick}>
        <Icon name={props.visible ? "navigate_open" : "navigate_close"} />
        {props.visible ? "schliessen" : "Menü"}
    </Button>
);

export enum DockPosition {
    TopLeft = "docked-top-left",
    TopRight = "docked-top-right",
    BottomLeft = "docked-bottom-left",
    BottomRight = "docked-bottom-right",
    None = ""
}

export enum WindowMode {
    Normal = "normal",
    Fullscreen = "fullscreen",
    Docked = "docked"
}

interface SidebarProps {
    uiStore: UiState;
    appStore: AppState;
    mapStore: MapState;
    appConfig: AppConfiguration;
    config: SidebarConfiguration;
    mapViewerConfig: MapViewerConfiguration;
    small?: boolean;
}

@observer
export class Sidebar extends React.Component<SidebarProps, any>{
    breakToDocked = 510;  // break point where to switch to docked mode
    autorunDisposers: any[] = [];
    lastActiveIndex: string[] = [];
    resizeObserver: ResizeObserver;

    widgetKey = "sidebar";
    sidebarRef = React.createRef<HTMLDivElement>();
    constructor(props: SidebarProps) {
        super(props);
        // noinspection PointlessBooleanExpressionJS,RedundantConditionalExpressionJS
        this.state = {
            activeIndex: props.config.activeIndex || ["toc"],
            width: this.props.small? "100%" : props.config.width
        };
        this.lastActiveIndex = props.config.activeIndex || ["toc"];
        this.checkHeight = this.checkHeight.bind(this);
        this.handleClick = this.handleClick.bind(this);
        this.handleResize = this.handleResize.bind(this);
        this.hide = this.hide.bind(this);
        this.toggleVisibility = this.toggleVisibility.bind(this);
    }
    handleResize(): void {
        this.checkHeight();
    }
    checkHeight(): void {
        const {uiStore} = this.props;
        const sHeight = this.sidebarRef.current?.clientHeight;
        const isHigh = (uiStore.uiHeight && sHeight && (uiStore.uiHeight - 95 < sHeight)) as boolean;
        if (uiStore.sidebarHigh !== isHigh) {
            uiStore.setSidebarHigh(isHigh);
        }
    }
    componentDidMount(): void {
        const {config, uiStore, small} = this.props;
        runInAction(() => {
            if (!uiStore.widgets.has(this.widgetKey)) {
                uiStore.addWidget(this.widgetKey, this);
                uiStore.setMenuVisible(!config.collapsed);
            }
        });
        if(small){
            this.autorunDisposers.push(autorun(() => {
                const {topicswitcherVisible} = this.props.uiStore;
                this.toggleTopicMenu(topicswitcherVisible);
            }));
        }else{
            this.checkHeight();
            this.resizeObserver = new ResizeObserver(() => {
                this.checkHeight();
            });
            if (this.sidebarRef.current){
                this.resizeObserver.observe(this.sidebarRef.current);
            }
            window.addEventListener("resize", this.handleResize);
        }
    }
    componentDidUpdate(prevProps: SidebarProps): void {
        const {uiStore} = this.props;
        if(prevProps.config.activeIndex !== this.props.config.activeIndex){
            this.setState({activeIndex: this.props.config.activeIndex || ["toc"]});
            this.lastActiveIndex = this.props.config.activeIndex || ["toc"];
        }
        if(prevProps.config.collapsed !== this.props.config.collapsed){
            uiStore.setMenuVisible(!this.props.config.collapsed);
        }
    }
    componentWillUnmount(): void {
        const {uiStore, small} = this.props;
        uiStore.removeWidget(this.widgetKey);
        this.autorunDisposers.forEach((disposer: any) => {
            disposer();
        });
        this.autorunDisposers.length = 0;
        if(!small){
            window.removeEventListener("resize", this.handleResize);
        }
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
    }

    /**
     * Handle clicks to accordion titles
     *
     * @param e
     * @param titleProps
     */
    handleClick(e: React.MouseEvent<HTMLDivElement>, titleProps: AccordionTitleProps): void {
        runInAction(()=> {
            const { activeIndex } = this.state;
            let newIndex: string[] = [];
            const index = titleProps.index as "basemap_gallery" | "legend" | "map_finder" | "print"
            | "sharing" | "toc" | "topic_menu" | "tools" | "tools_sharing";
            const isSmallDevice = window.matchMedia(
                "(max-width: "+ this.props.mapViewerConfig.breakpoints.md + "px)"
            ).matches;
            if (activeIndex.includes(index)) {
                // hide tab by removing index from list
                newIndex = activeIndex.filter((item: string) => item !== index);
            } else {
                // show panel by adding to list
                if (isSmallDevice || this.props.config.exclusivePanels?.includes(index)) {
                    // only open the exclusive tab
                    newIndex = [index as string];
                } else {
                    newIndex = [...activeIndex, index];
                }
            }
            if(!newIndex.includes("topic_menu")) {
                this.lastActiveIndex = newIndex;
            }
            this.setState( { activeIndex: newIndex });
            if(this.props.small) {
                this.props.uiStore.setTopicswitcherVisible(newIndex.includes("topic_menu"));
            }
        });
    }
    toggleTopicMenu(visible: boolean): void {
        runInAction(()=> {
            this.props.uiStore.setTopicswitcherVisible(visible);
            if(visible){
                this.setState({
                    activeIndex: ["topic_menu"]
                });
            }else{
                if(this.lastActiveIndex.length > 0) {
                    this.setState({activeIndex: this.lastActiveIndex});
                }
            }
        });
    }

    hide(): void {
        this.props.uiStore.setMenuVisible(false);
    }

    dock(): void {
        this.setState((state: any) => {
            if (state.currentMode !== WindowMode.Docked) {
                return {
                    currentMode: WindowMode.Docked,
                    dockPosition: DockPosition.TopRight,
                    width: "100vw"
                };
            }
        });
    }

    undock(): void {
        this.setState((state: any) => {
            if (state.currentMode !== WindowMode.Normal) {
                // go back to original size and position
                return {
                    currentMode: WindowMode.Normal,
                    dockPosition: DockPosition.None,
                    width: this.props.config.width
                };
            }
        });
    }

    toggleVisibility(): void {
        const currentVisible = this.props.uiStore.menuVisible;
        this.props.uiStore.setMenuVisible(!currentVisible);
    }

    render(): JSX.Element | null {
        const {activeIndex, width, currentMode} = this.state;
        const {appConfig, appStore, config, mapStore, mapViewerConfig, uiStore, small} = this.props;
        const {menuVisible, topicswitcherVisible} = uiStore;
        const controlsConfig = mapViewerConfig as SidebarControlsConfiguration;
        const panels = [];

        if (!config.disabledPanels.includes("toc")) {
            let content;
            if(mapViewerConfig.subtopics){
                content = (
                    <ViewManager
                        appConfig={appConfig}
                        appStore={appStore}
                        controlsConfig={controlsConfig}
                        mapStore={mapStore}
                        mapviewerConfig={mapViewerConfig}
                        small={!!small}
                        view={mapStore.view}
                        uiStore={uiStore} />
                );
            } else {
                content = (
                    <LayerList
                        appConfig={appConfig}
                        appStore={appStore}
                        controlsConfig={controlsConfig}
                        config={controlsConfig.layerList}
                        mapStore={mapStore}
                        small={!!small}
                        view={mapStore.view}
                        uiStore={uiStore} />
                );
            }
            panels.push({
                key: "toc",
                active: activeIndex.includes("toc"),
                title: {
                    content: (
                        <span>
                            <Icon name="navigate_plus" />
                            <Icon name="navigate_minus" />
                            <Icon name="map_roads" /> <span>Karteninhalt</span>
                        </span>),
                    icon: false,
                    index: "toc",
                    onClick: this.handleClick
                },
                content: {
                    content,
                }
            });
        }
        if (!config.disabledPanels.includes("legend")) {
            panels.push({
                key: "legend",
                active: activeIndex.includes("legend"),
                title: {
                    content: (<span>
                        <Icon name="navigate_plus" />
                        <Icon name="navigate_minus" />
                        <Icon name="list_style_bullets" /> <span>Legende</span>
                    </span>),
                    icon: false,
                    index: "legend",
                    onClick: this.handleClick
                },
                content: {
                    content: (controlsConfig.legend.useEsri && controlsConfig.legend.esriConfig)?(
                        <CoreLegend
                            uiStore={uiStore}
                            controlsConfig={controlsConfig}
                            appStore={appStore}
                            mapStore={mapStore}
                            view={mapStore.view}
                            config={controlsConfig.legend.esriConfig}
                            small={!!small}
                        />
                    ) :(
                        <Legend
                            appStore={appStore}
                            uiStore={uiStore}
                            config={controlsConfig.legend} />
                    ),
                }
            });
        }
        if (!config.disabledPanels.includes("print")) {
            panels.push({
                key: "print",
                active: activeIndex.includes("print"),
                title: {
                    content: (
                        <span>
                            <Icon name="navigate_plus" />
                            <Icon name="navigate_minus" />
                            <Icon name="printer" /> <span>Drucken</span>
                        </span>),
                    icon: false,
                    index: "print",
                    onClick: this.handleClick
                },
                content: {
                    content: (
                        <Printing
                            uiStore={uiStore}
                            view={mapStore.view}
                            config={controlsConfig.print}
                            appStore={appStore}
                            small={!!small}
                            visible={activeIndex.includes("print")}/>
                    )
                }
            });
        }
        if (!config.disabledPanels.includes("sharing") || !config.disabledPanels.includes("tools")) {
            const panelContent: JSX.Element[] = [];
            if (!config.disabledPanels.includes("sharing")){
                panelContent.push(<Sharing
                    key={"sharing"}
                    config={controlsConfig.print}
                    uiStore={uiStore}
                    view={mapStore.view}
                    appStore={appStore}
                    mapStore={mapStore}
                    appConfig={appConfig}
                    mapViewerConfig={mapViewerConfig}
                    small={!!small}
                    visible={activeIndex.includes("tools_sharing")
                        || activeIndex.includes("sharing") || activeIndex.includes("tools")}/>);
            }
            if (!config.disabledPanels.includes("sharing") && !config.disabledPanels.includes("tools")) {
                panelContent.push(<Divider key={"tool_divider"} className={"geo-tool-divider"}></Divider>);
            }
            if (!config.disabledPanels.includes("tools")) {
                panelContent.push(<div key={"tools"}><Tools
                    config={mapViewerConfig.tools}
                    uiStore={uiStore}
                    view={mapStore.view}
                    appStore={appStore}
                    small={!!small}/></div>);
            }
            panels.push({
                key: "tools_sharing",
                active: activeIndex.includes("sharing") || activeIndex.includes("tools")
                    || activeIndex.includes("tools_sharing"),
                title: {
                    content: (
                        <span>
                            <Icon name="navigate_plus" />
                            <Icon name="navigate_minus" />
                            <Icon name="share" /> <span>Teilen & Tools</span>
                        </span>),
                    icon: false,
                    index: "tools_sharing",
                    onClick: this.handleClick
                },
                content: {
                    content: panelContent
                }
            });
        }
        const cssProperties = { width };
        cssProperties["--panels"] = panels.length;
        if(small) {
            return <div
                className={`geo-sidebar geo-component geo-widget ${currentMode} geo-panels-${panels.length}`}
                style={cssProperties}
                ref={this.sidebarRef}>
                <Accordion
                    className={`geo-sidebar-accordion ${menuVisible || small ? "open" : "closed"}`}
                    styled fluid={currentMode === WindowMode.Docked}
                    exclusive={false}
                    panels={panels} />
            </div>;
        }
        return <div
            className={`geo-sidebar geo-component geo-widget ${currentMode} geo-panels-${panels.length}`}
            style={cssProperties}>
            <SemanticSidebar
                as={Menu}
                animation="overlay"
                direction={"top"}
                vertical
                visible={(menuVisible && !topicswitcherVisible)}>
                <div ref={this.sidebarRef}><Accordion
                    className=
                        {`geo-sidebar-accordion ${(menuVisible && !topicswitcherVisible) || small ? "open" : "closed"}`}
                    styled fluid={currentMode === WindowMode.Docked}
                    exclusive={false}
                    panels={panels} />
                <CollapseButton onClick={this.toggleVisibility} visible={(menuVisible && !topicswitcherVisible)} />
                </div>
            </SemanticSidebar>
            <CollapseButton onClick={this.toggleVisibility} visible={(menuVisible && !topicswitcherVisible)} />
        </div>;

    }
}
